import { draftsService } from 'services';
import io from 'socket.io-client';
import shortid from 'shortid';

// Action types
export const FETCH_DRAFTS_BEGIN = 'DRAFTS/FETCH_DRAFTS_BEGIN';
export const FETCH_DRAFTS_SUCCESS = 'DRAFTS/FETCH_DRAFTS_SUCCESS';
export const FETCH_DRAFTS_ERROR = 'DRAFTS/FETCH_DRAFTS_ERROR';
export const CREATE_DRAFT_BEGIN = 'DRAFTS/CREATE_DRAFT_BEGIN';
export const CREATE_DRAFT_SUCCESS = 'DRAFTS/CREATE_DRAFT_SUCCESS';
export const CREATE_DRAFT_ERROR = 'DRAFTS/CREATE_DRAFT_ERROR';
export const UPDATE_DRAFT_BEGIN = 'DRAFTS/UPDATE_DRAFT_BEGIN';
export const UPDATE_DRAFT_SUCCESS = 'DRAFTS/UPDATE_DRAFT_SUCCESS';
export const UPDATE_DRAFT_ERROR = 'DRAFTS/UPDATE_DRAFT_ERROR';
export const DELETE_DRAFT_SUCCESS = 'DRAFTS/DELETE_DRAFT_SUCCESS';
export const SEND_DRAFT_BEGIN = 'DRAFTS/SEND_DRAFT_BEGIN';
export const SEND_DRAFT_SUCCESS = 'DRAFTS/SEND_DRAFT_SUCCESS';
export const SEND_DRAFT_ERROR = 'DRAFTS/SEND_DRAFT_ERROR';
export const CONVERT_DRAFT_BEGIN = 'DRAFTS/CONVERT_DRAFT_BEGIN';
export const CONVERT_DRAFT_SUCCESS = 'DRAFTS/CONVERT_DRAFT_SUCCESS';
export const CONVERT_DRAFT_ERROR = 'DRAFTS/CONVERT_DRAFT_ERROR';
export const COMPLETE_DRAFT_BEGIN = 'DRAFTS/COMPLETE_DRAFT_BEGIN';
export const COMPLETE_DRAFT_SUCCESS = 'DRAFTS/COMPLETE_DRAFT_SUCCESS';
export const SELECT_DRAFT = 'DRAFTS/SELECT_DRAFT';
export const SEARCH_CUSTOMERS_SUCCESS = 'DRAFTS/SEARCH_CUSTOMERS_SUCCESS';
export const IMPORT_DRAFTS_BEGIN = 'DRAFTS/IMPORT_DRAFTS_BEGIN';
export const IMPORT_DRAFTS_SUCCESS = 'DRAFTS/IMPORT_DRAFTS_SUCCESS';
export const EXPORT_DRAFTS_BEGIN = 'DRAFTS/EXPORT_DRAFTS_BEGIN';
export const EXPORT_DRAFTS_SUCCESS = 'DRAFTS/EXPORT_DRAFTS_SUCCESS';
export const EXPORT_DRAFTS_ERROR = 'DRAFTS/EXPORT_DRAFTS_ERROR';
export const EXPORT_DRAFTS_STATUS = 'DRAFTS/EXPORT_DRAFTS_STATUS';

// Initial state
const initialState = {
  fetching: true,
  creating: false,
  sending: false,
  converting: false,
  convertError: null,
  importing: false,
  exporting: false,
  exportStatus: 0,
  exportError: null,
  needsReauth: false,
  list: [],
  totalCount: 0,
  selectedDraft: null,
  customers: [],
  filter: {}
};

// Action creators
export function fetchDraftsBegin(filter) {
  return { type: FETCH_DRAFTS_BEGIN, filter };
}

export function fetchDraftsSuccess(data) {
  return { type: FETCH_DRAFTS_SUCCESS, data };
}

export function fetchDraftsError(error) {
  return { type: FETCH_DRAFTS_ERROR, error };
}

export function selectDraft(draft) {
  return { type: SELECT_DRAFT, draft };
}

function normalizeDraftForForm(draft) {
  if (draft.line_items) {
    draft.line_items = draft.line_items.map(item => ({
      ...item,
      unit_price: item.unit_price || item.price || 0,
      discount_value: item.discount_value || item.discount_amount || 0,
      discount_type: item.discount_type || 'fixed',
      tax_rate: item.tax_rate || 0
    }));
  }
  if (Array.isArray(draft.shipping_lines) && draft.shipping_lines.length > 0) {
    draft.shipping_lines = draft.shipping_lines[0];
  } else if (Array.isArray(draft.shipping_lines)) {
    draft.shipping_lines = { title: '', price: '' };
  }
  return draft;
}

export function fetchDraft(id) {
  return (dispatch) => {
    draftsService().listDrafts({ filter: { id } }).then((res) => {
      let drafts = res.data.drafts || [];
      if (drafts.length > 0) {
        dispatch(selectDraft(normalizeDraftForForm(drafts[0])));
      }
    }).catch(() => {});
  };
}

// Thunk actions
export function fetchDrafts(filter) {
  return (dispatch) => {
    dispatch(fetchDraftsBegin(filter));
    draftsService().listDrafts(filter).then((res) => {
      return dispatch(fetchDraftsSuccess(res.data));
    }).catch((error) => {
      return dispatch(fetchDraftsError('Failed to load drafts. Please try again later.'));
    });
  };
}

export function createDraft(draft) {
  return (dispatch, getState) => {
    dispatch({ type: CREATE_DRAFT_BEGIN });
    return draftsService().createDraft(draft).then((res) => {
      dispatch({ type: CREATE_DRAFT_SUCCESS, data: res.data });
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
      return { ok: true };
    }).catch((error) => {
      let message = 'Failed to create draft. Please try again.';
      if (error && error.response && error.response.data && error.response.data.error) {
        message = error.response.data.error;
      }
      dispatch({ type: CREATE_DRAFT_ERROR, error: message });
      return { ok: false };
    });
  };
}

export function updateDraft(draft) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_DRAFT_BEGIN });
    return draftsService().updateDraft(draft).then((res) => {
      dispatch({ type: UPDATE_DRAFT_SUCCESS, data: res.data });
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
      return { ok: true };
    }).catch((error) => {
      let message = 'Failed to update draft. Please try again.';
      if (error && error.response && error.response.data && error.response.data.error) {
        message = error.response.data.error;
      }
      dispatch({ type: UPDATE_DRAFT_ERROR, error: message });
      return { ok: false };
    });
  };
}

export function deleteDraft(id) {
  return (dispatch, getState) => {
    draftsService().deleteDraft(id).then(() => {
      dispatch({ type: DELETE_DRAFT_SUCCESS, id });
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
    }).catch(() => {});
  };
}

export function sendDraft(id, templateName) {
  return (dispatch, getState) => {
    dispatch({ type: SEND_DRAFT_BEGIN });
    draftsService().sendDraft({ id, templateName }).then((res) => {
      dispatch({ type: SEND_DRAFT_SUCCESS, data: res.data });
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
    }).catch((error) => {
      dispatch({ type: SEND_DRAFT_ERROR, error });
    });
  };
}

export function deliverDraft(id) {
  return (dispatch, getState) => {
    draftsService().deliverDraft({ id }).then(() => {
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
    }).catch(() => {});
  };
}

export function validateCustomerForConvert(id) {
  return () => {
    return draftsService().validateCustomer({ id }).then((res) => res.data);
  };
}

export function convertDraft(id, skipCustomerCreation = false) {
  return (dispatch) => {
    dispatch({ type: CONVERT_DRAFT_BEGIN });
    draftsService().convertDraft({ id, skipCustomerCreation }).then((res) => {
      dispatch({ type: CONVERT_DRAFT_SUCCESS, data: res.data });
    }).catch((error) => {
      let errorMsg = 'Failed to push to Shopify';
      if (error.response && error.response.data) {
        if (error.response.data.customerErrors) {
          errorMsg = error.response.data.customerErrors.map(e => e.message).join(', ');
        } else if (error.response.data.error) {
          errorMsg = error.response.data.error;
        }
      }
      dispatch({ type: CONVERT_DRAFT_ERROR, error: errorMsg });
    });
  };
}

export function completeDraft(id) {
  return (dispatch) => {
    dispatch({ type: COMPLETE_DRAFT_BEGIN });
    draftsService().completeDraft({ id }).then((res) => {
      dispatch({ type: COMPLETE_DRAFT_SUCCESS, data: res.data });
    }).catch(() => {});
  };
}

export function searchCustomers(query) {
  return (dispatch) => {
    draftsService().searchCustomers(query).then((res) => {
      dispatch({ type: SEARCH_CUSTOMERS_SUCCESS, data: res.data });
    }).catch(() => {});
  };
}

export function importShopifyDrafts() {
  return (dispatch, getState) => {
    dispatch({ type: IMPORT_DRAFTS_BEGIN });
    draftsService().importShopifyDrafts().then((res) => {
      dispatch({ type: IMPORT_DRAFTS_SUCCESS, data: res.data });
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
    }).catch(() => {});
  };
}

export function duplicateDraft(id) {
  return (dispatch, getState) => {
    draftsService().duplicateDraft({ id }).then(() => {
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
    }).catch(() => {});
  };
}

export function issueManualInvoice(id) {
  return (dispatch, getState) => {
    draftsService().issueManualInvoice({ id }).then(() => {
      const state = getState().drafts;
      dispatch(fetchDrafts(state.filter));
    }).catch(() => {});
  };
}

let draftsSessionId = shortid.generate();
let draftsSocket = null;
if (typeof location !== 'undefined') {
  draftsSocket = io({
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 15,
    transports: ['websocket', 'polling']
  });
}

let draftsMsg = null;

export function exportDraftsFileDataReceived(msg) {
  return (dispatch) => {
    var arrBuffer = msg.data;
    var newBlob;
    var format = msg.format;
    var fileName = msg.fileName;

    if (format === 'pdf') {
      newBlob = new Blob([arrBuffer], { type: 'application/pdf' });
    }
    if (format === 'xlsx') {
      newBlob = new Blob([arrBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    }
    if (format === 'xml') {
      newBlob = new Blob([arrBuffer], { type: 'application/xml' });
    }

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(newBlob, `${fileName}.${format}`);
      dispatch({ type: EXPORT_DRAFTS_SUCCESS });
      dispatch({ type: EXPORT_DRAFTS_STATUS, status: 0 });
      return;
    }

    const data = window.URL.createObjectURL(newBlob);
    var link = document.createElement('a');
    link.href = data;
    link.download = `${fileName}.${format}`;
    link.setAttribute('download', `${fileName}.${format}`);
    document && document.body.appendChild(link);
    link.click();
    setTimeout(function () {
      window.URL.revokeObjectURL(data);
    }, 100);

    dispatch({ type: EXPORT_DRAFTS_SUCCESS });
    dispatch({ type: EXPORT_DRAFTS_STATUS, status: 0 });
  };
}

export function initDraftsSocket() {
  return (dispatch) => {
    if (!draftsSocket) return;

    draftsSocket.on('download_status_' + draftsSessionId, function (m) {
      setTimeout(function () { draftsMsg = m; }, 100);
    });

    draftsSocket.on('download_error_' + draftsSessionId, function (m) {
      dispatch({ type: EXPORT_DRAFTS_ERROR, error: m.err });
    });

    draftsSocket.on('file_data_' + draftsSessionId, function (m) {
      dispatch(exportDraftsFileDataReceived(m));
      setTimeout(function () { draftsMsg = null; }, 5000);
    });

    setInterval(() => {
      if (draftsMsg !== null)
        dispatch({ type: EXPORT_DRAFTS_STATUS, status: draftsMsg.status });
    }, 1000);
  };
}

export function exportDraftsBulk(selected, settings) {
  return (dispatch) => {
    dispatch({ type: EXPORT_DRAFTS_BEGIN });

    let ids = [];
    for (var id in selected) {
      if (selected[id] === true) ids.push(id);
    }

    let data = {
      ids: ids.join(','),
      settings,
      sessionId: draftsSessionId
    };

    if (!data.ids && settings.what === 'selected')
      return dispatch({ type: EXPORT_DRAFTS_SUCCESS });

    draftsService().exportDraftsBulk(data).then(() => {
    }).catch((error) => {
      dispatch({ type: EXPORT_DRAFTS_ERROR, error: error.response ? error.response.data : 'Export failed' });
    });
  };
}

// Reducer
const reducer = (state = initialState, action = {}) => {
  switch (action.type) {

    case FETCH_DRAFTS_BEGIN:
      return {
        ...state,
        fetching: true,
        filter: action.filter
      };
    case FETCH_DRAFTS_SUCCESS:
      return {
        ...state,
        fetching: false,
        list: action.data.drafts || [],
        totalCount: action.data.totalCount || 0
      };
    case FETCH_DRAFTS_ERROR:
      return {
        ...state,
        fetching: false,
        list: [],
        totalCount: 0
      };

    case CREATE_DRAFT_BEGIN:
      return {
        ...state,
        creating: true,
        saveError: null
      };
    case CREATE_DRAFT_SUCCESS:
      return {
        ...state,
        creating: false,
        saveError: null
      };
    case CREATE_DRAFT_ERROR:
      return {
        ...state,
        creating: false,
        saveError: action.error
      };

    case UPDATE_DRAFT_BEGIN:
      return {
        ...state,
        creating: true,
        saveError: null
      };
    case UPDATE_DRAFT_SUCCESS:
      return {
        ...state,
        creating: false,
        saveError: null
      };
    case UPDATE_DRAFT_ERROR:
      return {
        ...state,
        creating: false,
        saveError: action.error
      };

    case DELETE_DRAFT_SUCCESS:
      return {
        ...state,
        list: state.list.filter(d => d.id !== action.id)
      };

    case SEND_DRAFT_BEGIN:
      return {
        ...state,
        sending: true
      };
    case SEND_DRAFT_SUCCESS:
      return {
        ...state,
        sending: false
      };
    case SEND_DRAFT_ERROR:
      return {
        ...state,
        sending: false
      };

    case CONVERT_DRAFT_BEGIN:
      return {
        ...state,
        converting: true,
        convertError: null
      };
    case CONVERT_DRAFT_SUCCESS:
      return {
        ...state,
        converting: false,
        convertError: null
      };
    case CONVERT_DRAFT_ERROR:
      return {
        ...state,
        converting: false,
        convertError: action.error
      };

    case COMPLETE_DRAFT_BEGIN:
      return {
        ...state,
        converting: true
      };
    case COMPLETE_DRAFT_SUCCESS:
      return {
        ...state,
        converting: false
      };

    case SELECT_DRAFT:
      return {
        ...state,
        selectedDraft: action.draft
      };

    case SEARCH_CUSTOMERS_SUCCESS:
      return {
        ...state,
        customers: (action.data && action.data.customers) || []
      };

    case IMPORT_DRAFTS_BEGIN:
      return {
        ...state,
        importing: true
      };
    case IMPORT_DRAFTS_SUCCESS:
      return {
        ...state,
        importing: false,
        needsReauth: action.data && action.data.needsReauth ? true : false
      };

    case EXPORT_DRAFTS_BEGIN:
      return {
        ...state,
        exporting: true,
        exportStatus: 0,
        exportError: null
      };
    case EXPORT_DRAFTS_SUCCESS:
      return {
        ...state,
        exporting: false,
        exportStatus: 0
      };
    case EXPORT_DRAFTS_ERROR:
      return {
        ...state,
        exporting: false,
        exportError: action.error
      };
    case EXPORT_DRAFTS_STATUS:
      return {
        ...state,
        exportStatus: action.status
      };

    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// app/modules/drafts.js